import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants } from './dashboardMotion';
import { database } from '../firebaseConfig';
import { ref, onValue, update, remove, push, serverTimestamp } from 'firebase/database';
import { EMAIL_TEMPLATES_PATH, phaseOptions, genTplId, labelForSuperStatus } from './emailTemplatesConfig';
import './EmailTemplateSettings.css';

// Firebase-Vorlage -> Formular-Entwurf. Strings für Inputs, mindestens je eine
// leere Zeile für Stil-Beispiele und Drop-Felder, damit der Editor nie „leer" wirkt.
const toDraft = (t = {}) => {
  const styleSamples = (Array.isArray(t.styleSamples) ? t.styleSamples : (t.styleSamples ? Object.values(t.styleSamples) : []))
    .map((s) => String(s || ''));
  const inputSections = (Array.isArray(t.inputSections) ? t.inputSections : (t.inputSections ? Object.values(t.inputSections) : []))
    .filter((s) => s && typeof s === 'object')
    .map((s) => ({ id: String(s.id || genTplId()), label: String(s.label || ''), required: s.required === true }));
  const showIn = (Array.isArray(t.showIn) ? t.showIn : (t.showIn ? Object.values(t.showIn) : []))
    .map((x) => String(x || '')).filter(Boolean);
  return {
    name: t.name || '',
    aiHint: t.aiHint || '',
    styleSamples: styleSamples.length ? styleSamples : [''],
    inputSections: inputSections.length ? inputSections : [{ id: genTplId(), label: '', required: false }],
    versions: typeof t.versions === 'number' ? String(t.versions) : '3',
    showIn,
    quickButton: t.quickButton !== false,
    numbered: t.numbered === true,
    active: t.active !== false,
  };
};

const emptyDraft = () => ({
  name: '',
  aiHint: '',
  styleSamples: [''],
  inputSections: [{ id: genTplId(), label: '', required: false }],
  versions: '3',
  showIn: [],
  quickButton: true,
  numbered: false,
  active: true,
});

const clampVersions = (s) => Math.max(2, Math.min(5, Math.round(Number(s) || 3)));

// Beispiel-Vorlage „Update-Mail" (Stilvorlage + Drop-Felder), die man mit einem
// Klick in das Neu-Formular lädt. Dient als sofort nutzbarer Start (Update 1).
const UPDATE_MAIL_SAMPLE = `Moin Olaf, moin Henning,

ich wollte euch kurz ein paar Updates zu unserem Outreach-Projekt schicken, damit ihr auf dem aktuellen Stand seid.

In den letzten Tagen hat sich einiges getan:
* Der Aufwärmprozess der E-Mail-Postfächer ist inzwischen so weit fortgeschritten, dass ich ab jetzt in vollem Umfang versenden kann. Damit sind nun die 60 Mails pro Tag möglich.
* Zusätzlich haben wir zwei weitere Kontaktlisten importiert. Darin befinden sich Unternehmen mit Hochbau-Fokus im Umkreis von 100 Kilometern rund um Hamburg.
* Patryk hat Video-Erklärungen zur Plattform erhalten, damit er eingehende Antworten zeitnah und passend beantworten kann.

Bei zwei Punkten bräuchte ich kurz euer Feedback:

1. Branchenfremde Kontakte in der Schlummerkunden-Liste
In der Schlummerkunden-Liste befinden sich einige Kontakte, die nicht direkt aus der Baubranche kommen. Soll ich solche Einträge aus der Liste entfernen oder trotzdem kontaktieren?

2. Zusätzliche Kampagnen-Idee
Statt in der ersten Mail direkt mit "Habt ihr ein Projekt für uns?" einzusteigen, könnten wir ausgewählten Unternehmen zunächst eine "exklusive Partnerschaft" anbieten. Die Hürde für eine erste Rückmeldung dürfte dadurch geringer sein. Passt es für euch, wenn ich diesen Ansatz zusätzlich teste?

Ich werde heute mit voller Kapazität versenden und bin zuversichtlich, dass bis Ende der Woche die ersten Antworten reinkommen.

Alles Liebe aus Wien, Jakub`;

const updateMailExample = () => ({
  name: 'Update-Mail',
  aiHint: 'Kurzes, ehrliches Projekt-Update an die Ansprechpartner zum laufenden Outreach-Projekt. Locker-professionell, per du/ihr, auf Augenhöhe, ohne Marketing-Floskeln. Aufbau: kurze Einleitung, dann "In den letzten Tagen hat sich einiges getan" mit Stichpunkten, optional ein kurzer Feedback-/Fragen-Block an die Kunden, optional zusätzliche Ideen oder Next Steps, freundlicher Abschluss mit Gruß.',
  styleSamples: [UPDATE_MAIL_SAMPLE],
  inputSections: [
    { id: genTplId(), label: 'Was hat sich in den letzten Tagen getan?', required: true },
    { id: genTplId(), label: 'Fragen & Feedback', required: false },
    { id: genTplId(), label: 'Zusätzliche Idee oder Next Steps', required: false },
  ],
  versions: '3',
  showIn: ['experimental'],
  quickButton: true,
  numbered: true,
  active: true,
});

const draftToPayload = (d) => ({
  name: d.name.trim().slice(0, 120),
  aiHint: d.aiHint.trim().slice(0, 6000),
  styleSamples: d.styleSamples.map((s) => String(s || '').trim()).filter(Boolean).map((s) => s.slice(0, 12000)),
  inputSections: d.inputSections
    .filter((s) => String(s.label || '').trim())
    .map((s) => ({ id: String(s.id || genTplId()), label: String(s.label).trim().slice(0, 300), required: s.required === true })),
  versions: clampVersions(d.versions),
  showIn: Array.isArray(d.showIn) ? d.showIn.filter(Boolean) : [],
  quickButton: d.quickButton !== false,
  numbered: d.numbered === true,
  active: d.active !== false,
  updatedAt: serverTimestamp(),
});

// Schicker iOS-artiger Schalter (statt nackter Checkbox).
const Switch = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`ets-switch ${checked ? 'ets-switch-on' : ''}`}
    onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
    disabled={disabled}
  >
    <span className="ets-switch-knob" />
  </button>
);

// Options-Zeile: Titel + Erklärsatz links, Schalter rechts.
const OptionRow = ({ title, desc, checked, onChange }) => (
  <div className={`ets-option ${checked ? 'ets-option-on' : ''}`} onClick={() => onChange(!checked)}>
    <div className="ets-option-text">
      <span className="ets-option-title">{title}</span>
      {desc && <span className="ets-option-desc">{desc}</span>}
    </div>
    <Switch checked={checked} onChange={onChange} label={title} />
  </div>
);

const EmailTemplateSettings = () => {
  const [templates, setTemplates] = useState({});
  const [drafts, setDrafts] = useState({});
  const [newDraft, setNewDraft] = useState(emptyDraft());
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // welche Vorlagen-Karten aufgeklappt sind

  const phases = useMemo(() => phaseOptions(), []);
  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  useEffect(() => {
    const unsub = onValue(ref(database, EMAIL_TEMPLATES_PATH), (snap) => {
      const val = snap.val() || {};
      setTemplates(val);
      setDrafts((prev) => {
        const next = {};
        Object.keys(val).forEach((id) => {
          next[id] = prev[id] || toDraft(val[id]);
        });
        return next;
      });
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const setField = (id, field, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const isDirty = (id) =>
    templates[id] && JSON.stringify(toDraft(templates[id])) !== JSON.stringify(drafts[id]);

  const saveTemplate = async (id) => {
    const d = drafts[id];
    if (!d || !d.name.trim()) return;
    setSavingId(id);
    try {
      await update(ref(database, `${EMAIL_TEMPLATES_PATH}/${id}`), draftToPayload(d));
    } catch (e) {
      console.error('Vorlage speichern fehlgeschlagen:', e);
    } finally {
      setSavingId(null);
    }
  };

  const resetTemplate = (id) =>
    setDrafts((prev) => ({ ...prev, [id]: toDraft(templates[id]) }));

  const deleteTemplate = async (id) => {
    const name = templates[id]?.name || 'diese Vorlage';
    if (!window.confirm(`„${name}" wirklich löschen?`)) return;
    try {
      await remove(ref(database, `${EMAIL_TEMPLATES_PATH}/${id}`));
      setDrafts((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (e) {
      console.error('Vorlage löschen fehlgeschlagen:', e);
    }
  };

  const addTemplate = async () => {
    if (!newDraft.name.trim()) return;
    try {
      const node = push(ref(database, EMAIL_TEMPLATES_PATH));
      await update(node, { ...draftToPayload(newDraft), createdAt: serverTimestamp() });
      // Neu angelegte Vorlage direkt aufgeklappt zeigen, damit sie sofort sichtbar ist.
      if (node.key) setExpanded((p) => ({ ...p, [node.key]: true }));
      setNewDraft(emptyDraft());
    } catch (e) {
      console.error('Vorlage anlegen fehlgeschlagen:', e);
    }
  };

  const ids = useMemo(
    () => Object.keys(templates).sort((a, b) => (templates[a]?.createdAt || 0) - (templates[b]?.createdAt || 0)),
    [templates]
  );

  // ── Editor-Felder (für „neu" und bestehende Vorlagen identisch) ──
  const renderFields = (d, onChange) => {
    // Stil-Beispiele bearbeiten
    const setSample = (i, v) => onChange('styleSamples', d.styleSamples.map((s, idx) => (idx === i ? v : s)));
    const addSample = () => onChange('styleSamples', [...d.styleSamples, '']);
    const removeSample = (i) => onChange('styleSamples', d.styleSamples.length > 1 ? d.styleSamples.filter((_, idx) => idx !== i) : ['']);

    // Drop-Felder bearbeiten
    const setSection = (i, patch) => onChange('inputSections', d.inputSections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
    const addSection = () => onChange('inputSections', [...d.inputSections, { id: genTplId(), label: '', required: false }]);
    const removeSection = (i) => onChange('inputSections', d.inputSections.length > 1 ? d.inputSections.filter((_, idx) => idx !== i) : [{ id: genTplId(), label: '', required: false }]);

    const toggleShowIn = (sid) => onChange('showIn', d.showIn.includes(sid) ? d.showIn.filter((x) => x !== sid) : [...d.showIn, sid]);

    return (
      <>
        <div className="ets-step">
          <div className="ets-step-head"><span className="ets-step-num">1</span> Grundlagen</div>
          <div className="ets-row">
            <label className="ets-field ets-field-grow">
              <span>Name der Vorlage</span>
              <input type="text" value={d.name} placeholder="z. B. Update-Mail" onChange={(e) => onChange('name', e.target.value)} />
              <small className="ets-inline-hint">Interner Name – steht so auf dem Button und im Detail (z. B. „Update-Mail").</small>
            </label>
            <div className="ets-field ets-field-versions">
              <span>Anzahl Versionen</span>
              <div className="ets-seg5" role="group" aria-label="Anzahl Versionen">
                {[2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`ets-seg5-btn ${clampVersions(d.versions) === n ? 'ets-seg5-btn-on' : ''}`}
                    onClick={() => onChange('versions', String(n))}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <small className="ets-inline-hint">Mindestens 2. Version 1 bleibt möglichst nah an deiner ersten Stil-Vorlage, jede weitere wird etwas kreativer (andere Winkel) – bleibt aber beim Thema. Du wählst danach die beste aus.</small>
            </div>
          </div>
          <label className="ets-field">
            <span>KI-Hinweis <small>(worum geht es, Sonderregeln – optional)</small></span>
            <textarea
              className="ets-textarea ets-textarea-sm"
              value={d.aiHint}
              placeholder="z. B. Kurzes Projekt-Update an die Ansprechpartner. Locker-professionell, per du/ihr. Keine erfundenen Zahlen."
              onChange={(e) => onChange('aiHint', e.target.value)}
            />
          </label>
        </div>

        <div className="ets-step">
          <div className="ets-step-head"><span className="ets-step-num">2</span> Stil-Vorlage</div>
          <p className="ets-step-desc">Eine beispielhafte E-Mail, an deren Ton, Aufbau und Länge sich die KI orientiert.</p>
          {d.styleSamples.map((s, i) => (
            <div key={i} className="ets-sample">
              <textarea
                className="ets-textarea"
                value={s}
                placeholder="Hier eine beispielhafte E-Mail einfügen, an deren Stil & Aufbau sich die KI halten soll …"
                onChange={(e) => setSample(i, e.target.value)}
              />
              <button type="button" className="ets-icon-btn" onClick={() => removeSample(i)} title="Beispiel entfernen" aria-label="Beispiel entfernen">×</button>
            </div>
          ))}
          <button type="button" className="ets-add-row" onClick={addSample}>+ Weiteres Stil-Beispiel</button>
        </div>

        <div className="ets-step">
          <div className="ets-step-head"><span className="ets-step-num">3</span> Drop-Felder</div>
          <p className="ets-step-desc">Die Eingabe-Felder, in die du beim Generieren die frischen Infos reindroppst. Mit „Pflicht" markierte Felder müssen vor dem Generieren ausgefüllt sein.</p>
          {d.inputSections.map((s, i) => (
            <div key={s.id} className="ets-section-row">
              <input
                type="text"
                className="ets-section-label"
                value={s.label}
                placeholder="z. B. Was hat sich in den letzten Tagen getan?"
                onChange={(e) => setSection(i, { label: e.target.value })}
              />
              <div className="ets-section-req">
                <span className="ets-section-req-lbl">Pflicht</span>
                <Switch checked={s.required === true} onChange={(v) => setSection(i, { required: v })} label="Pflichtfeld" />
              </div>
              <button type="button" className="ets-icon-btn" onClick={() => removeSection(i)} title="Feld entfernen" aria-label="Feld entfernen">×</button>
            </div>
          ))}
          <button type="button" className="ets-add-row" onClick={addSection}>+ Drop-Feld</button>
        </div>

        <div className="ets-step">
          <div className="ets-step-head"><span className="ets-step-num">4</span> Wo &amp; wie anzeigen</div>
          <p className="ets-step-desc">Wähle die Pipeline-Phasen, in denen dieser Generator auftauchen soll. Ein Kontakt sieht den Button nur, solange er sich in einer der gewählten Phasen befindet.</p>
          <div className="ets-phase-grid">
            {phases.map((p) => {
              const on = d.showIn.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`ets-phase-card ${on ? 'ets-phase-card-on' : ''}`}
                  onClick={() => toggleShowIn(p.id)}
                  aria-pressed={on}
                >
                  <span className="ets-phase-check" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </span>
                  <span className="ets-phase-info">
                    <span className="ets-phase-name">{p.label}</span>
                    <span className="ets-phase-sub">{p.phaseLabel}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ets-options">
            <OptionRow
              title="Quick-Button in der Karten-Vorschau"
              desc="Blendet direkt auf der Kontakt-Karte in der Pipeline einen Button ein, über den du diese Mail mit einem Klick öffnest und generierst – ohne den Kontakt erst zu öffnen. Aus: die Mail gibt es nur im Detail des Kontakts."
              checked={d.quickButton !== false}
              onChange={(v) => onChange('quickButton', v)}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="ets-wrap">
      <motion.div className="ets-inner" variants={staggerContainer} initial="initial" animate="animate">
        <motion.header className="ets-head" variants={itemVariants}>
          <div>
            <h1>E-Mail-Vorlagen</h1>
            <p>Lege wiederverwendbare KI-Generatoren an: ein Stil-Beispiel, welche Infos du beim Generieren eingibst und in welcher Phase der Button erscheint. Im Kontakt droppst du dann nur noch die Infos und bekommst mehrere fertige Versionen zum Copy-Pasten.</p>
          </div>
        </motion.header>

        {/* Neue Vorlage anlegen */}
        <motion.section className="ets-card ets-card-new" variants={itemVariants}>
          <div className="ets-card-top">
            <div className="ets-card-title">Vorlage hinzufügen</div>
            <button type="button" className="ets-example-btn" onClick={() => setNewDraft(updateMailExample())} title="Das Formular mit der Beispiel-Vorlage „Update-Mail“ befüllen">
              Beispiel „Update-Mail“ laden
            </button>
          </div>
          {renderFields(newDraft, (field, value) => setNewDraft((p) => ({ ...p, [field]: value })))}
          <div className="ets-card-foot">
            <button type="button" className="ets-btn ets-btn-primary" onClick={addTemplate} disabled={!newDraft.name.trim()}>
              + Hinzufügen &amp; speichern
            </button>
          </div>
        </motion.section>

        {/* Bestehende Vorlagen */}
        <motion.div className="ets-list-head" variants={itemVariants}>Vorlagen ({ids.length})</motion.div>
        {loading ? (
          <div className="ets-empty">Laden…</div>
        ) : ids.length === 0 ? (
          <div className="ets-empty">Noch keine Vorlagen. Lege oben die erste an (z. B. „Update-Mail").</div>
        ) : (
          ids.map((id) => {
            const d = drafts[id] || toDraft(templates[id]);
            const dirty = isDirty(id);
            const isOpen = !!expanded[id];
            const showIn = d.showIn || [];
            return (
              <motion.section key={id} variants={itemVariants} className={`ets-card ets-card-collapsible ${d.active === false ? 'ets-card-inactive' : ''} ${isOpen ? 'ets-card-open' : ''}`}>
                <button type="button" className="ets-summary" onClick={() => toggleExpand(id)} aria-expanded={isOpen}>
                  <div className="ets-summary-main">
                    <span className="ets-summary-name">
                      {d.name || 'Unbenannte Vorlage'}
                      {dirty && <span className="ets-summary-dot" title="Ungespeicherte Änderungen" />}
                    </span>
                    <div className="ets-summary-tags">
                      {showIn.length === 0
                        ? <span className="ets-summary-tag ets-summary-tag-warn">Keine Phase gewählt</span>
                        : showIn.map((sid) => <span key={sid} className="ets-summary-tag">{labelForSuperStatus(sid)}</span>)}
                      <span className="ets-summary-tag ets-summary-tag-soft">{clampVersions(d.versions)} Versionen</span>
                      {d.numbered && <span className="ets-summary-tag ets-summary-tag-soft">nummeriert</span>}
                    </div>
                  </div>
                  <div className="ets-summary-right">
                    <span className={`ets-summary-state ${d.active === false ? 'ets-summary-state-off' : ''}`}>{d.active === false ? 'inaktiv' : 'aktiv'}</span>
                    <svg className={`ets-chevron ${isOpen ? 'ets-chevron-open' : ''}`} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                  </div>
                </button>

                {isOpen && (
                  <div className="ets-card-edit">
                    <div className="ets-card-top">
                      <span className="ets-badge">{showIn.length} {showIn.length === 1 ? 'Phase' : 'Phasen'}</span>
                      <div className="ets-active-toggle">
                        <span>{d.active !== false ? 'Aktiv' : 'Inaktiv'}</span>
                        <Switch checked={d.active !== false} onChange={(v) => setField(id, 'active', v)} label="Vorlage aktiv" />
                      </div>
                    </div>
                    {renderFields(d, (field, value) => setField(id, field, value))}
                    <div className="ets-card-foot">
                      <div className="ets-card-actions">
                        {dirty && <button type="button" className="ets-btn ets-btn-ghost" onClick={() => resetTemplate(id)}>Verwerfen</button>}
                        <button type="button" className="ets-btn ets-btn-del" onClick={() => deleteTemplate(id)}>Löschen</button>
                        <button type="button" className="ets-btn ets-btn-primary" onClick={() => saveTemplate(id)} disabled={!dirty || savingId === id}>
                          {savingId === id ? 'Speichert…' : dirty ? 'Speichern' : 'Gespeichert'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.section>
            );
          })
        )}
      </motion.div>
    </div>
  );
};

export default EmailTemplateSettings;
